import { ExclamationCircleOutlined } from '@ant-design/icons';
import { BackButton } from '@components/shared/buttons/BackButton';
import { IsolatedModal } from '@components/shared/modals/IsolatedModal';
import { PharmacyFinder } from '@components/shared/modals/pharmacyFinder/PharmacyFinder';
import { ajaxByUser } from '@helper/api';
import { DateFormat } from '@helper/formatHelpers';
import { getEssityApiClient } from '@services/EssityApi';
import {
	CreateNoteResponseDto,
	OrderDto,
	OrderState,
	PharmacyDto,
	PharmacyTaxNoteCreateResponse,
	PharmacyTaxNoteDetailsDto,
	PharmacyTaxNoteUpdateResponse,
	TaxNoteSource,
} from '@services/src/models';
import {
	Button,
	Col,
	Modal,
	Row,
	Space,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Field, Formik, FormikActions, FormikProps } from 'formik';
import {
	FCheckbox,
	FDatePicker,
	FFieldLabel,
	FInput,
	FInputNumber,
	FTextArea,
} from 'forms/FormikFormItems';
import { showWarning } from 'layout/Modals';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import styles from '../styles/PharmacyNote.module.less';
import AttachmentUploader, { IAttachmentState } from '@components/shared/attachmentUploader/AttachmentUploader';
import { PharmacyNoteAddOrdersModal } from '../addOrders/PharmacyNoteAddOrdersModal';

interface IProps {
	readOnly: boolean;
	noteId?: string;
	pharmacyId?: string;
	setAttachment: (attachment: Blob | undefined) => void;
	editMode?: boolean;
}


interface IState {
	noteDate?: Moment;
	noteNumber?: number;
	noteValue?: number;
	year?: Moment;
	month?: Moment;
	ordersValue?: number;
	settledOrdersValue?: number;
	isDocumentationComplete?: boolean;
	note?: string;
}

export const CreditNoteDetails = (props: IProps) => {
	const [pharmacy, setPharmacy] = useState<PharmacyDto>();
	const [orders, setOrders] = useState<OrderDto[]>([]);
	const [note, setNote] = useState<PharmacyTaxNoteDetailsDto>();
	const [suggestedOrders, setSuggestedOrders] = useState<OrderDto[]>();
	const [isBusy, setIsBusy] = useState<boolean>();
	const [state, setState] = useState<IState>({
		year: moment(),
		month: moment(
			new Date(
				moment().year(),
				moment().add(-1, 'month').month(),
				moment().day()
			)
		),
		isDocumentationComplete: true,
	});

	const history = useHistory();

	useEffect(() => {
		if (props.noteId) {
			(async () => {
				const api = await getEssityApiClient();
				const result = await api.pharmacyTaxNote.getById(props.noteId!);
				setNote(result);
				setPharmacy(result.pharmacy);

				if (result.documentsData && result.documentsData?.length > 0 && result.documentsData[0].data) {
					const blob = new Blob([
						new Uint8Array(
							result.documentsData[0].data,
							result.documentsData[0].data.byteOffset,
							result.documentsData[0].data.length
						),
					]);
					props.setAttachment(new File([blob], `${result.documentsData[0].fileName}`, { type: result.documentsData[0].mimeType }));
				}


				setOrders(result.orders as OrderDto[]);
			})();
		}
	}, [props.noteId]);

	useEffect(() => {
		if (props.pharmacyId) {
			(async () => {
				const api = await getEssityApiClient();
				const result = await api.pharmacy.getById(props.pharmacyId!);
				setPharmacy(result);
			})();
		}
	}, [props.pharmacyId]);

	useEffect(() => {
		(async () => {
			if (state.year && state.month) {
				const api = await getEssityApiClient();

				const result = await api.order.getAllNotAssignedOrdersForPharmacyInDateScope(
					{
						pharmacyId: pharmacy?.id,
						year: state.year!.year(),
						month: state.month!.month() + 1,
						includeOrdersAssignedToPharmacyTaxNoteId: undefined,
					}
				);

				setSuggestedOrders(result);
			}
		})();
	}, [state.month, state.year, pharmacy]);

	const getInitialValues = () => {
		if (note) {
			const dateObject = new Date(
				moment().year(),
				note.month!,
				moment().day()
			);

			return {
				pharmacyName: pharmacy?.name,
				placeId: pharmacy?.placeId,
				noteDate: moment(note?.noteDate),
				noteNumber: note?.noteNumber,
				noteValue: note?.noteValue,
				ordersValue: note?.assignedOrdersValue,
				settledOrdersValue: note?.settledOrdersValue,
				taxNoteType: note?.taxNoteType,
				month: moment(dateObject).add(-1, 'month'),
				year: moment(
					new Date(note!.year!, moment().month(), moment().day())
				),
				isDocumentationComplete: note?.isDocumentationComplete,
				note: note?.note,
			};
		} else {
			const month = moment(
				new Date(
					moment().year(),
					moment().add(-1, 'month').month(),
					moment().day()
				)
			);

			const year = moment();

			return {
				pharmacyName: pharmacy?.name,
				placeId: pharmacy?.placeId,
				month: state?.month ? state?.month : month,
				year: state?.year ? state.year : year,
				noteDate: state?.noteDate,
				noteNumber: state.noteNumber,
				noteValue: state.noteValue,
				ordersValue: state.ordersValue,
				isDocumentationComplete: state.isDocumentationComplete,
				note: state.note,
				settledOrdersValue: state?.settledOrdersValue,
			};
		}
	};

	const hasSelectedOrders = (): boolean => {
		if (!orders) {
			return false;
		}

		return orders.length > 0;
	};

	const isFieldDisabled = () => (props.readOnly && !props.editMode);

	const RenderNoteDetails = (formikProps: FormikProps<any>) => {
		return (
			<>
				<Row>
					<Col span={24}>
						<Row>

							{pharmacy && <Col span={10}  >
								<Row >
									{pharmacy?.name}
								</Row>
								<Row >
									{pharmacy?.street}, {pharmacy?.city}
								</Row>
								<Row >
									NIP: {pharmacy?.taxNumber}
								</Row>
								<Row >
									Login: {pharmacy?.login}
								</Row>
								<Row >
									Kod Kom: {pharmacy?.kodKom}
								</Row>
								<Row >
									{pharmacy?.phoneNumber} [{pharmacy?.paymentMail}]
								</Row>
								<Row >
									CNA: {pharmacy?.automaticNoteSending ? 'Tak' : 'Nie'}
								</Row>
							</Col>}
						</Row>
					</Col>
				</Row>

				<Row>
					<Col span={10}>
						<FFieldLabel label="SAP ID" />
						<Field
							component={FInput}
							placeholder="SAP ID"
							name="placeId"
							readOnly={true}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={10}>
						<FFieldLabel label="Note date" />
						<Field
							label="Note date"
							component={FDatePicker}
							name="noteDate"
							mode="date"
							style={{ width: '100%' }}
							disabled={true}
							changeData={(value: any) => {
								setState({
									...state,
									noteDate: value,
								});
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={10}>
						<FFieldLabel label="Note number" />
						<Field
							component={FInput}
							changeData={(value: any) => {
								setState({ ...state, noteNumber: value });
							}}
							placeholder="Note number"
							name="noteNumber"
							readOnly={true}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={5} style={{ paddingRight: 5 }}>
						<FFieldLabel label="Month" />
						<Field
							component={FDatePicker}
							placeholder="Month"
							name="month"
							style={{ width: '100%' }}
							inputReadOnly={
								props.readOnly || hasSelectedOrders()
							}
							changeData={(value: any) => {
								setState({ ...state, month: value });
							}}
							picker="month"
							format={DateFormat.Month}
						/>
					</Col>
					<Col span={5} style={{ paddingLeft: 5 }}>
						<FFieldLabel label="Year" />
						<Field
							component={FDatePicker}
							placeholder="Year"
							style={{ width: '100%' }}
							name="year"
							inputReadOnly={
								props.readOnly || hasSelectedOrders()
							}
							changeData={(value: any) => {
								setState({ ...state, year: value });
							}}
							picker="year"
							format={DateFormat.Year}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={10}>
						<FFieldLabel label="Note value" />
						<Field
							component={FInputNumber}
							placeholder="Note value"
							min={Number.MIN_SAFE_INTEGER}
							name="noteValue"
							readOnly={true}
							changeData={(value: number) => {
								setState({ ...state, noteValue: value });
							}}
						/>
					</Col>
				</Row>

				<Row>
					<Col span={10}>
						<FFieldLabel label="Orders value" />
						<Row>
							<Col span={12} style={{ paddingRight: 5 }}>
								<Field
									component={FInputNumber}
									placeholder="Orders value"
									name="ordersValue"
									min={Number.MIN_SAFE_INTEGER}
									readOnly={true}
								/>
							</Col>
							<Col span={12} style={{ paddingLeft: 5 }}>
								{pharmacy && (
									<PharmacyNoteAddOrdersModal
										readOnly={true}
										year={formikProps.values['year']}
										month={formikProps.values['month']}
										pharmacyId={pharmacy.id!}
										suggestedOrders={suggestedOrders}
										noteValue={
											formikProps.values['noteValue']
										}
										onOrdersSelect={(
											orders: OrderDto[]
										) => {
											setOrders(orders);

											const refundSum = _.sum(
												orders?.map((x) => x.refund!)
											);
											const refundSetteledSum = _.sum(
												orders?.filter(x => x.orderState == OrderState.SettledNhf).map((x) => x.refund!)
											);

											formikProps.setFieldValue(
												'ordersValue',
												refundSum.toFixed(2)
											);
											formikProps.setFieldValue(
												'settledOrdersValue',
												refundSum.toFixed(2)
											);

											setState({
												...state,
												ordersValue: refundSum,
												settledOrdersValue: refundSetteledSum,
											});
										}}
										selectedOrders={orders}
										pharmacyTaxNoteId={props.noteId}
									/>
								)}
							</Col>
						</Row>
					</Col>
				</Row>
				<Row>
					<Col span={10}>
						<FFieldLabel label="Setteled orders value" />
						<Row>
							<Col span={12} style={{ paddingRight: 5 }}>
								<Field
									component={FInputNumber}
									placeholder="Setteled orders value"
									name="settledOrdersValue"
									min={Number.MIN_SAFE_INTEGER}
									readOnly={true}
								/>
							</Col>

						</Row>
					</Col>
				</Row>
				<Row>
					<Col span={16}>
						<FFieldLabel label="Note" />
						<Field
							component={FTextArea}
							placeholder="Note"
							style={{ resize: 'none' }}
							maxLength={5000}
							name="note"
							rows={5}
							maxRows={5}
							changeData={(value: string) => {
								setState({ ...state, note: value });
							}}
							readOnly={props.readOnly && !props.editMode}
						/>
					</Col>
				</Row>
			</>
		);
	};


	const updateNote = async (values: any) => {

		setIsBusy(true);


		if (props.editMode) {
			ajaxByUser('Successfully updated note.', () =>
				getEssityApiClient().then((api) =>
					api.pharmacyTaxNote
						.updateCredit({
							body: {
								note: values.note,
								pharmacyTaxNoteId: props.noteId,
							},
						})
						.then((response: PharmacyTaxNoteUpdateResponse) => {
							history.push(
								`/pharmacyPayments/${response.pharmacyTaxNoteGroupId}/${response.pharmacyId}`
							);
						})
						.finally(() => setIsBusy(false))
				)
			);
		}
	};

	const confirmValue = (pharmacyTaxNoteId: string, path: string): void => {
		setIsBusy(true);
		ajaxByUser('Successfully confirmed value.', () =>
			getEssityApiClient().then((api) =>
				api.pharmacyTaxNote
					.assignBalancedPaymentConfirmation({
						body: {
							pharmacyTaxNoteId: pharmacyTaxNoteId,
						},
					})
					.finally(() => {
						setIsBusy(false);
						history.push(path);
					})
			)
		);
	};

	return (
		<Space direction="vertical" className={styles.details}>
			<h2>Credit Note Details</h2>
			{note?.internalNumber && (
				<h3>Credit note {note?.internalNumber}</h3>
			)}
			<Space direction="vertical" className={styles.form}>
				<Formik
					validateOnChange={false}
					validateOnBlur={true}
					initialValues={getInitialValues()}
					enableReinitialize
					onSubmit={(values, actions: FormikActions<any>) =>
						updateNote(values)
					}
					render={(formikProps: FormikProps<any>) => (
						<Space direction="vertical" style={{ width: '100%' }}>
							{RenderNoteDetails(formikProps)}
							<Space
								direction="horizontal"
								className={styles.actions}
							>
								<BackButton />
								<Button
									type="primary"
									shape="round"
									size="large"
									className={styles.save}
									disabled={
										(props.readOnly && !props.editMode) ||
										isBusy
									}
									onClick={() => formikProps.submitForm()}
								>
									Save
								</Button>
							</Space>
						</Space>
					)}
				></Formik>
			</Space>
		</Space>
	);
};
